import * as React from 'react';
import styled, { css } from 'styled-components';

import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';
import { BootstrapModalConfirm, Col, MenuItem, DeleteMenuItem } from 'components/bootstrap';
import { useGetLinkedProceduresForStep, useDeleteEventProcedureStep } from 'security-app/hooks/useEventProceduresAPI';
import { ConfirmDialog, IconButton, Icon, IfPermitted, HasOwnership } from 'components/common';
import { DropdownMenu } from 'security-app/components/common';
import { useModalContext } from 'common/contexts';
import EntityShareModal from 'components/permissions/EntityShareModal';

type Props = {
  eventProcedureStep: EventProcedureStepAPIType;
};

const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
  `,
);

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  padding: 0.6rem;
  gap: 0.3rem;
`;

function RowActionMenu({ eventProcedureStep }: Props) {
  const { setModal, setEntity } = useModalContext();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = React.useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = React.useState(false);
  const { linkedEventProcedures, loadingLinkedEventProcedures } = useGetLinkedProceduresForStep(eventProcedureStep.id);
  const { deleteEventProcedureStep } = useDeleteEventProcedureStep();

  const onEdit = () => {
    setEntity(eventProcedureStep);
    setModal('EDIT-EVENT-PROCEDURE-STEP');
  };

  const onDelete = async () => {
    await deleteEventProcedureStep({ id: eventProcedureStep.id });
    setShowDeleteModal(false);
  };

  const handleDelete = () => {
    if (loadingLinkedEventProcedures) return;
    if (linkedEventProcedures.length < 1) {
      setShowDeleteModal(true);
    } else {
      setShowDeleteWarningModal(true);
    }
  };

  const toggleEntityShareModal = React.useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);

  return (
    <Wrapper>
      <DropdownMenu
        trigger={<IconButton name="more_horiz" title="Toggle event procedure actions" />}
        position="bottom-end">
        <IfPermitted permissions={[`event_procedure_steps:edit:${eventProcedureStep.id}`]}>
          <MenuItem onClick={onEdit}>
            <Icon name="edit_square" size="xs" /> Edit
          </MenuItem>
        </IfPermitted>
        <HasOwnership id={eventProcedureStep.id} type="event_procedure_step">
          {({ disabled: hasMissingPermissions }) => (
            <MenuItem disabled={hasMissingPermissions} onClick={toggleEntityShareModal}>
              <Icon name="person_add" size="xs" /> Share
            </MenuItem>
          )}
        </HasOwnership>
        <IfPermitted permissions={[`event_procedure_steps:delete:${eventProcedureStep.id}`]}>
          <DeleteMenuItem onClick={() => handleDelete()}>
            <Icon name="delete" size="xs" /> Delete
          </DeleteMenuItem>
        </IfPermitted>
      </DropdownMenu>
      <BootstrapModalConfirm
        title="Are you sure you want to delete this step?"
        onConfirm={onDelete}
        onCancel={() => setShowDeleteModal(false)}
        showModal={showDeleteModal}>
        <div>You are about to delete this event procedure step: {eventProcedureStep.title}</div>
      </BootstrapModalConfirm>
      <ConfirmDialog
        show={showDeleteWarningModal}
        title="This step is currently being used in procedures"
        onConfirm={() => setShowDeleteWarningModal(false)}
        hideCancelButton
        btnConfirmText="Okay">
        <div>This step is currently being used in the following procedures:</div>
        <StyledCol>
          {linkedEventProcedures.map((procedure: string) => (
            <strong key={`procedure-item-${procedure}`}>&bull; {procedure}</strong>
          ))}
        </StyledCol>
        <div>If you want to delete this step please first remove them from the procedures above.</div>
      </ConfirmDialog>
      {showEntityShareModal && (
        <EntityShareModal
          entityId={eventProcedureStep.id}
          entityType="event_procedure_step"
          entityTypeTitle="Event Procedure Step"
          entityTitle={eventProcedureStep.title}
          showShareableEntityURL={false}
          description="Search for a User or Team to add as collaborator on this event procedure step."
          onClose={toggleEntityShareModal}
        />
      )}
    </Wrapper>
  );
}
export default RowActionMenu;
